import { useState } from 'react'
import { Alert, Button, ButtonGroup, Dropdown, DropdownButton, Form, Modal, Spinner} from 'react-bootstrap'
import { FiUserPlus, FiMail, FiShield, FiX } from 'react-icons/fi'
import { IOrg } from '../../models/org'
import { useMutation, useQueryClient } from 'react-query';
import { inviteMember } from '../../api/org';

type Props = {
    org: IOrg;
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
}

function InviteMember({org, show, setShow}: Props) {
    const [email, setEmail] = useState("")
    const [role, setRole] = useState("viewer")
    const [error, setError] = useState("")

    const handleClose = () => setShow(false);
    //   const handleShow = () => setShow(true);
    
    const queryClient = useQueryClient();
    const { mutateAsync: addMemberFn, isLoading } = useMutation(
        inviteMember, // mutation function goes here
        {
            onSuccess: () => {
                queryClient.invalidateQueries('OrgInvitations');
                handleClose();
            },
            onError: (error: any) => {
                setError("Unexpected error. Please try again or contact admin.");
            },
        }
    );

    const handleAdd = async (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        if (!email) {
            setError("Please enter email address.");
        }
        
        addMemberFn({
            orgId: org.id,
            email,
            role
        })
    }

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton className="bg-dark border-secondary">
                <Modal.Title className="text-white d-flex align-items-center">
                    <FiUserPlus className="me-2" size={20} />
                    Add Member
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleAdd}>
                <Modal.Body className="bg-dark text-white">
                    
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor='email' className="text-white fw-semibold">
                            <FiMail className="me-2" size={16} />
                            Email Address*
                        </Form.Label>
                        <Form.Control
                            id='email'
                            type='email'
                            value={email}
                            required
                            placeholder='Enter email address'
                            className="bg-dark border-secondary text-white"
                            style={{ borderColor: '#6c757d' }}
                            onChange={(e) => setEmail(e.target.value)}/>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label className="text-white fw-semibold">
                            <FiShield className="me-2" size={16} />
                            Member Role
                        </Form.Label>
                        <div className='d-flex align-items-center' style={{gap:'1em'}}>
                            <DropdownButton
                                as={ButtonGroup}
                                variant='outline-light'
                                title="Select Role"
                                className="">
                                <Dropdown.Menu className="bg-dark border-secondary">
                                    <Dropdown.Item onClick={() => setRole("viewer")} className="text-white">Viewer</Dropdown.Item>
                                    <Dropdown.Item onClick={() => setRole("editor")} className="text-white">Editor</Dropdown.Item>
                                    <Dropdown.Item onClick={() => setRole("admin")} className="text-white">Admin</Dropdown.Item>
                                    <Dropdown.Item onClick={() => setRole("billing")} className="text-white">Billing</Dropdown.Item>
                                </Dropdown.Menu>
                            </DropdownButton>
                            <span className="text-light fw-semibold text-capitalize">{role}</span>
                        </div>
                    </Form.Group>
                
                    {error && (
                        <Alert 
                            variant="danger" 
                            className="mt-3 d-flex align-items-center"
                            style={{ 
                                backgroundColor: 'rgba(220, 53, 69, 0.2)', 
                                borderColor: '#dc3545',
                                color: '#ffffff'
                            }}
                        >
                            <FiX className="me-2" size={16} />
                            {error}
                        </Alert>
                    )}
                    
                    <Form.Group className='py-2'>
                        <Form.Text className="text-light">
                            Added members have 7 days to accept invitation sent to their email before it expires.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="bg-dark border-secondary">
                    <Button variant="outline-light" onClick={handleClose}>
                        Close
                    </Button>
                    <Button variant="primary" type='submit' disabled={isLoading}>
                        {isLoading ? <Spinner size="sm" className="me-2" /> : <FiUserPlus className="me-2" />}
                        {isLoading ? "Adding..." : "Add Member"}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export default InviteMember